/**
 * liveScore.js — Live game state cells shared by the Odds and Props grids.
 *
 * Score records arrive on the 'scores' store key (one per fixture, written
 * by scores_stream.py). Each cell subscribes on its own, so a clock tick
 * re-renders only that cell — never a row builder or a whole grid.
 */

import { useState, useEffect, useCallback } from 'react';
import { Box, Typography } from '@mui/material';
import dataStore from './dataStore';

const SX_COL_OUTER = { display: 'flex', flexDirection: 'column', gap: '0', justifyContent: 'center', height: '100%' };

// Fixture ids the score feed currently reports in play. An odds row's own
// isLive flag (fixture_is_live) only changes when that row is republished,
// so right after kickoff, or for a book that never moved its price, rows can
// still say pregame while the clock is running. Grids combine both.
export function liveFixtureIds() {
  const ids = new Set();
  for (const s of dataStore.get('scores').values()) {
    if (s && s.fixture_id && s.is_live && (s.status || '').toLowerCase() !== 'completed') ids.add(s.fixture_id);
  }
  return ids;
}

export function useFixtureScore(fixtureId) {
  const read = useCallback(
    () => (fixtureId ? dataStore.get('scores').get(fixtureId) : undefined) || null,
    [fixtureId],
  );
  const [score, setScore] = useState(read);
  useEffect(() => {
    setScore(read());
    return dataStore.subscribe('scores', () => {
      setScore(prev => { const next = read(); return next === prev ? prev : next; });
    });
  }, [read]);
  return score;
}

// "Q4 6:56", "H2 12:30", "P3 4:10", "Top 7", "2H 67:00", "Set 2", "Half", "Final"
export function formatGameClock(s) {
  if (!s) return '';
  const status = (s.status || '').toLowerCase();
  if (status === 'completed') return 'Final';
  if (status === 'half') return 'Half';
  if (status && status !== 'live') return status.charAt(0).toUpperCase() + status.slice(1);

  const raw = s.period != null ? String(s.period).trim() : '';
  const n = Number(s.period_number) || (/^\d+$/.test(raw) ? Number(raw) : 0);
  const clock = s.clock ? String(s.clock).trim() : '';
  const sport = (s.sport || '').toLowerCase();
  const league = (s.league || '').toLowerCase();

  let label = '';
  if (raw && !/^\d+$/.test(raw)) {
    label = raw.length <= 4 ? raw.toUpperCase() : raw;         // feed already says "OT", "HT", ...
  } else if (!n) {
    label = '';
  } else if (sport === 'football') {
    label = n <= 4 ? `Q${n}` : (n === 5 ? 'OT' : `${n - 4}OT`);
  } else if (sport === 'basketball') {
    if (league === 'ncaab') label = n <= 2 ? `H${n}` : (n === 3 ? 'OT' : `${n - 2}OT`);
    else label = n <= 4 ? `Q${n}` : (n === 5 ? 'OT' : `${n - 4}OT`);
  } else if (sport === 'hockey') {
    label = n <= 3 ? `P${n}` : (n === 4 ? 'OT' : 'SO');
  } else if (sport === 'baseball') {
    const half = /^bot/i.test(clock) ? 'Bot' : (/^top/i.test(clock) ? 'Top' : '');
    return half ? `${half} ${n}` : `Inn ${n}`;
  } else if (sport === 'soccer') {
    label = n <= 2 ? `${n}H` : 'ET';
  } else if (sport === 'tennis') {
    return `Set ${n}`;
  } else {
    label = `P${n}`;
  }
  // Between periods the feed sends text instead of a time ("End of 4th
  // Quarter"), which doesn't fit the column. Anything that isn't a time and
  // doesn't fit is dropped in favour of the period label alone.
  if (clock) {
    if (/^end/i.test(clock)) return `End ${label}`.trim();
    if (!/^\d{1,3}:\d{2}/.test(clock) && clock.length > 8) return label;
  }
  return clock ? `${label} ${clock}`.trim() : label;
}

// Date/time column body. While a game is live the date gives way to the
// period and clock above the LIVE badge; a finished game shows FINAL.
export function LiveStatusCell({ fixtureId, isLive, isThreeWay, date, time, isMobile }) {
  const s = useFixtureScore(fixtureId);
  const status = (s?.status || '').toLowerCase();
  const isFinal = status === 'completed';
  const inPlay = !!s && !isFinal && (s.is_live || isLive);
  const clockText = inPlay ? formatGameClock(s) : '';
  const fs = isMobile ? '12px' : '14px';
  const cellSx = { height: '35px', display: 'flex', alignItems: 'center', paddingLeft: isMobile ? '0px' : '8px', paddingRight: isMobile ? '4px' : '8px' };
  return (
    <Box sx={SX_COL_OUTER}>
      <Box sx={cellSx}>
        {clockText
          ? <Typography sx={{ fontSize: fs, fontWeight: 600, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>{clockText}</Typography>
          : <Typography sx={{ fontSize: fs }}>{date}</Typography>}
      </Box>
      {isThreeWay && <Box sx={{ height: '35px' }} />}
      <Box sx={cellSx}>
        {isFinal ? (
          <Typography sx={{ fontSize: isMobile ? '10px' : '11px', fontWeight: 600, color: '#9a9a9a', letterSpacing: '0.5px' }}>
            FINAL
          </Typography>
        ) : (isLive || inPlay) ? (
          <Box sx={{
            display: 'inline-flex', alignItems: 'center', gap: '4px',
            px: '6px', py: '1px', borderRadius: '4px',
            backgroundColor: 'rgba(239, 68, 68, 0.15)',
            border: '1px solid rgba(239, 68, 68, 0.5)',
          }}>
            <Box sx={{
              width: '6px', height: '6px', borderRadius: '50%',
              backgroundColor: '#ef4444',
              animation: 'pulse 1.5s ease-in-out infinite',
              '@keyframes pulse': {
                '0%, 100%': { opacity: 1 },
                '50%': { opacity: 0.4 },
              },
            }} />
            <Typography sx={{ fontSize: isMobile ? '10px' : '11px', fontWeight: 600, color: '#ef4444', letterSpacing: '0.5px' }}>
              LIVE
            </Typography>
          </Box>
        ) : (
          <Typography sx={{ fontSize: fs }}>{time}</Typography>
        )}
      </Box>
    </Box>
  );
}
